import React, { forwardRef, useMemo } from 'react';
import { Box } from '@mui/material';
import type { ColDef, RowClassRules } from 'ag-grid-community';
import AgGrid from '../../../components/ag-grid/ag-grid';
import type { CityStateRow } from './types';
import { isRowInvalid } from './utils';

/** Props for {@link CityStateGrid}. */
interface CityStateGridProps {
    rows: CityStateRow[];
    onSelectionChanged: () => void;
}

/**
 * The AgGrid instance for the City/State detail popup: column definitions (inline-editable
 * `status`/`cityAbbreviation`; `description` is editable except on a genuinely existing record
 * (`isExist = true`, not redirected via `_forceUpdateExisting`) since only that row's
 * `cityAbbreviation` change is ever persisted — see `CityStateDetailPopup.handleConfirmMerge`;
 * `stateName`/`country` are normally read-only but become editable for the whole session on any
 * row that was **originally invalid** (`_originallyInvalid`, snapshotted when the row was loaded
 * — see below), so operators can fill in — and keep correcting — a missing value before merging),
 * row selection (every row, including `Exists = Yes`, has a functional checkbox), and row styling
 * (bold for rows whose original CityAbbreviation was populated, red for invalid rows, blue for
 * redirected updates).
 *
 * Forwards the underlying `AgGrid` ref so the parent can drive `api.getSelectedRows()`,
 * `api.forEachNode()`, etc.
 */
const CityStateGrid = forwardRef<any, CityStateGridProps>(({ rows, onSelectionChanged }, ref) => {
    const defaultColDef = useMemo<ColDef>(() => ({
        sortable: true,
        resizable: true,
        flex: 1,
        minWidth: 90,
        filter: false,
        suppressMovable: true,
    }), []);

    /**
     * Bold rows whose original (server) CityAbbreviation was already populated; red-highlight
     * rows that fail the `stateId`/`stateName`/`country` validity checks.
     */
    const rowClassRules = useMemo<RowClassRules>(() => ({
        'city-state-row-bold': (params) => !!(params.data as CityStateRow)?._originalCityAbbreviation,
        'city-state-row-invalid': (params) => isRowInvalid(params.data as CityStateRow),
        'city-state-row-update': (params) => params.data && !!(params.data as CityStateRow)?._forceUpdateExisting,
    }), []);

    const colDefs: ColDef[] = [
        {
            headerCheckboxSelection: true,
            // Every row — including Exists = Yes — keeps a functional selection checkbox so it can
            // be merged; for Exists = Yes rows the merge only persists their cityAbbreviation
            // change (see CityStateDetailPopup.handleConfirmMerge).
            checkboxSelection: true,
            maxWidth: 50, sortable: false, resizable: false, flex: 0,
        },
        { field: 'marketType', headerName: 'Market Type', maxWidth: 130 },
        { field: 'city', headerName: 'City' },
        { field: 'stateId', headerName: 'State', maxWidth: 100 },
        // StateName/Country are normally read-only: both are auto-populated from the
        // venue_city_states match (see InventoryDropAnalysisUtils.QueryOrphanedCityStates) and
        // should stay in sync with that lookup rather than be hand-edited per row. The one
        // exception is a row that was originally invalid (per `_originallyInvalid`, snapshotted
        // when the row was loaded — see CityStateDetailPopup) — that stays editable for the whole
        // session so the operator can supply the missing data (and keep correcting it) before
        // merging. Editability is decided solely by the row's *original* invalid state, not a live
        // `isRowInvalid` check and not the field's current emptiness, so the cell does not revert
        // to read-only once the operator types a value that makes the row valid.
        {
            field: 'stateName', headerName: 'State Name',
            editable: (params: any) => !!(params.data as CityStateRow)?._originallyInvalid,
        },
        {
            field: 'country', headerName: 'Country', maxWidth: 100,
            editable: (params: any) => !!(params.data as CityStateRow)?._originallyInvalid,
        },
        {
            field: 'isExist', headerName: 'Exists', maxWidth: 110,
            valueFormatter: (params: any) => (params.data as CityStateRow)?._forceUpdateExisting ? 'Yes (Update)' : (params.value ? 'Yes' : 'No'),
            cellStyle: (params: any) => (params.data as CityStateRow)?._forceUpdateExisting
                ? { color: '#1565c0', fontWeight: 600 }
                : { color: params.value ? '#2e7d32' : '#d32f2f', fontWeight: 600 },
        },
        { field: 'eventCount', headerName: 'Events', maxWidth: 100 },
        {
            field: 'description', headerName: 'Description',
            // Disabled for a genuinely existing record (isExist = true, not redirected via
            // _forceUpdateExisting): CityStateDetailPopup.handleConfirmMerge only ever submits
            // that row's cityAbbreviation change, so editing description here would be silently
            // discarded on merge.
            editable: (params: any) => !((params.data as CityStateRow)?.isExist && !(params.data as CityStateRow)?._forceUpdateExisting),
        },
        {
            field: 'status', headerName: 'Status', maxWidth: 100,
            // Disable editing for a genuinely existing record (isExist = true, not redirected via
            // _forceUpdateExisting): CityStateDetailPopup.handleConfirmMerge only ever submits
            // that row's cityAbbreviation change, so editing status here would be silently
            // discarded on merge.
            editable: (params: any) => !((params.data as CityStateRow)?.isExist && !(params.data as CityStateRow)?._forceUpdateExisting),
            valueParser: (params: any) => Number(params.newValue),
        },
        { field: 'cityAbbreviation', headerName: 'City Abbreviation', editable: true },
    ];

    return (
        <Box sx={{ height: 500, width: '100%' }}>
            <AgGrid
                ref={ref}
                isRaw
                rowData={rows}
                columnDefs={colDefs}
                defaultColDef={defaultColDef}
                rowClassRules={rowClassRules}
                rowSelection="multiple"
                suppressRowClickSelection
                onSelectionChanged={onSelectionChanged}
                getRowId={(params: any) => `${params.data.marketType}|${params.data.city}|${params.data.stateId}`}
                rowHeight={40}
                hidePaging
                gridHeight="500px"
            />
        </Box>
    );
});

CityStateGrid.displayName = 'CityStateGrid';

export default CityStateGrid;
